import { Fragment, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon } from './Icons';
import { sortRows, textOf, valueOf, type SortVal } from './tableSort';

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'live' | 'mute';
export const TONE_FG: Record<Tone, string> = { ok: 'var(--ok)', warn: 'var(--conf-mid)', bad: 'var(--conf-low)', info: 'var(--brand)', live: 'var(--live)', mute: 'var(--ink-62)' };

export type Severity = 'action' | 'watch' | 'info';
export const SEVERITY: Record<Severity, { label: string; tone: Tone; fg: string; bg: string; icon: string }> = {
  action: { label: 'ACTION', tone: 'bad', fg: 'var(--conf-low)', bg: 'var(--conf-low-soft)', icon: 'warn' },
  watch: { label: 'WATCH', tone: 'warn', fg: 'var(--conf-mid)', bg: 'var(--conf-mid-soft)', icon: 'warn' },
  info: { label: 'INFO', tone: 'mute', fg: 'var(--ink-62)', bg: 'var(--fill-04)', icon: 'info' },
};

export function Alert({ severity, text, sub, acts }: { severity: Severity; text: ReactNode; sub?: ReactNode; acts?: Act[] }) {
  const s = SEVERITY[severity];
  return <Block bg={s.bg}><Banner icon={s.icon} fg={s.fg} text={text} sub={sub} acts={acts} /></Block>;
}

export function Pill({ tone = 'mute', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return <span className={`pill pill--${tone}`} title={title}>{children}</span>;
}

export function ClassChip({ cls, title, shrink }: { cls?: string | null; title?: string; shrink?: boolean }) {
  const k = cls === 'vnni' || cls === 'avx2-zen3' || cls === 'avx2' ? cls : 'none';
  const n = { vnni: 3, 'avx2-zen3': 2, avx2: 1, none: 0 }[k];
  const label = { vnni: 'vnni · Genoa', 'avx2-zen3': 'avx2-zen3 · Milan', avx2: 'avx2 · Rome', none: 'unprobed' }[k];
  return (
    <span className={`cls cls--${k} ${shrink ? 'cls--shrink' : ''}`} title={title ?? (k === 'none' ? 'Class not known yet. Probe CPU to read it.' : undefined)}>
      <span className="cls__bars">{[4, 7, 10].map((h, i) => <span key={i} className="cls__bar" style={{ height: h, opacity: i < n ? 1 : 0.22 }} />)}</span>
      {shrink ? <span className="cls__label">{label}</span> : label}
    </span>
  );
}

export function Dot({ tone, hollow }: { tone: Tone; hollow?: boolean }) {
  return <span className="adm__dot" style={hollow ? { border: '1.5px solid var(--ink-40)', background: 'transparent' } : { background: TONE_FG[tone] }} />;
}

export interface Act { label: string; onClick: () => void; danger?: boolean; title?: string; primary?: boolean; disabled?: boolean }

export function Acts({ acts, size = 'xs' }: { acts?: Act[]; size?: 'xs' | 'sm' }) {
  if (!acts?.length) return null;
  return (
    <div className="tbl__acts" onClick={(e) => e.stopPropagation()}>
      {acts.map((a) => (
        <button key={a.label} type="button" className={`btn btn--${size} ${a.danger ? 'btn--danger' : ''} ${a.primary ? 'btn--primary' : ''}`} title={a.title} onClick={a.onClick} disabled={a.disabled}>{a.label}</button>
      ))}
    </div>
  );
}

export interface Col { label: string; width: string; align?: 'r'; sort?: string; sortAsc?: boolean }
export interface SortState { key: string; dir: 'asc' | 'desc' }
export interface Cell { v?: number | string | null; t?: ReactNode; sub?: ReactNode; mono?: boolean; fg?: string; fw?: number; pill?: { label: string; tone: Tone }; cls?: string | null; dot?: Tone; hollow?: boolean; acts?: Act[]; node?: ReactNode }
export interface Row { key: string; cells: Cell[]; onClick?: () => void; bg?: string; meta?: string; expanded?: boolean }

export function CellView({ c, right }: { c: Cell; right?: boolean }) {
  return (
    <div className={`tbl__cell ${right ? 'tbl__cell--r' : ''}`}>
      {c.dot && <Dot tone={c.dot} hollow={c.hollow} />}
      {c.pill && <Pill tone={c.pill.tone}>{c.pill.label}</Pill>}
      {c.cls !== undefined && (c.t !== undefined || c.sub !== undefined) ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: right ? 'flex-end' : 'flex-start', gap: 3, minWidth: 0, maxWidth: '100%' }}>
          <ClassChip cls={c.cls} shrink />
          {c.t !== undefined && c.t !== '' && <div className={`tbl__t ${c.mono ? 'mono' : ''}`} style={{ color: c.fg ?? 'var(--ink-96)', fontWeight: c.fw ?? 400, maxWidth: '100%' }}>{c.t}</div>}
          {c.sub !== undefined && <div className="tbl__s" style={{ maxWidth: '100%' }}>{c.sub}</div>}
        </div>
      ) : c.cls !== undefined && <ClassChip cls={c.cls} shrink />}
      {c.node}
      {c.cls === undefined && (c.t !== undefined || c.sub !== undefined) && (
        <div style={{ flex: '1 1 auto', minWidth: 0, textAlign: right ? 'right' : 'left' }}>
          {c.t !== undefined && <div className={`tbl__t ${c.mono ? 'mono' : ''}`} style={{ color: c.fg ?? 'var(--ink-96)', fontWeight: c.fw ?? 400 }}>{c.t}</div>}
          {c.sub !== undefined && <div className="tbl__s">{c.sub}</div>}
        </div>
      )}
      {c.acts && <Acts acts={c.acts} />}
    </div>
  );
}

export interface Tab { key: string; label: string; count?: number | string; bad?: boolean }
export interface Chip { label: string; on: boolean; onClick: () => void }
export interface Select { value: string; options: { value: string; label: string }[]; onChange: (v: string) => void; title?: string }

export interface BlockProps {
  title?: string;
  sub?: ReactNode;
  count?: ReactNode;
  asOf?: string | null;
  stale?: boolean;
  asleep?: boolean;
  onWake?: () => void;
  note?: ReactNode;
  headActs?: Act[];
  csv?: () => void;
  tabs?: Tab[];
  tab?: string;
  onTab?: (k: string) => void;
  chips?: Chip[];
  selects?: Select[];
  search?: { value: string; placeholder: string; onChange: (v: string) => void };
  extra?: ReactNode;
  loading?: boolean;
  error?: { title: string; text?: string; retry?: () => void } | null;
  empty?: ReactNode;
  emptyGood?: boolean;
  foot?: ReactNode;
  span?: number;
  children?: ReactNode;
  style?: CSSProperties;
  bg?: string;
}

export function Block(p: BlockProps) {
  const head = p.title !== undefined;
  return (
    <section className="blk" style={{ ...(p.bg ? { background: p.bg, borderColor: 'transparent' } : {}), ...p.style }} aria-label={p.title}>
      {head && (
        <div className="blk__head">
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span className="blk__title">{p.title}</span>
              {p.count !== undefined && <span className="blk__count">{p.count}</span>}
            </div>
            {p.sub && <div className="blk__sub">{p.sub}</div>}
          </div>
          <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {p.stale && <span className="blk__stale" title="Older than two polling intervals">STALE</span>}
            {p.asOf && <span className="blk__asof">as of {p.asOf}</span>}
            {p.headActs?.map((a) => <button key={a.label} type="button" className={`btn btn--sm ${a.primary ? 'btn--primary' : ''} ${a.danger ? 'btn--danger' : ''}`} onClick={a.onClick} title={a.title} disabled={a.disabled}>{a.label}</button>)}
            {p.csv && <button type="button" className="btn btn--xs" style={{ color: 'var(--ink-62)', letterSpacing: '.06em', fontFamily: 'var(--vs-font-mono)', fontSize: 10 }} onClick={p.csv} title="Export CSV"><Icon name="download" size={12} />CSV</button>}
          </div>
        </div>
      )}
      {p.asleep && (
        <div className="blk__strip"><Dot tone="mute" hollow /><span style={{ flex: 1 }}>Database idle, showing cached data.</span>{p.onWake && <button type="button" className="btn btn--link" style={{ fontSize: 11.5 }} onClick={p.onWake}>Wake database</button>}</div>
      )}
      {p.note && <div className="blk__strip" style={{ background: 'var(--fill-04)', fontWeight: 300 }}><Icon name="info" size={13} /><span style={{ flex: 1, textWrap: 'pretty' }}>{p.note}</span></div>}
      {p.tabs && (
        <div className="blk__tabs" role="tablist">
          {p.tabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={p.tab === t.key} className={`blk__tab ${p.tab === t.key ? 'blk__tab--on' : ''}`} onClick={() => p.onTab?.(t.key)}>
              {t.label}{t.count !== undefined && t.count !== '' && <span className="pill" style={{ padding: '1px 5px', borderRadius: 4, background: t.bad ? 'var(--conf-low-soft)' : 'var(--fill-08)', color: t.bad ? 'var(--conf-low)' : 'var(--ink-62)' }}>{t.count}</span>}
            </button>
          ))}
        </div>
      )}
      {(p.chips?.length || p.selects?.length || p.search || p.extra) && (
        <div className="blk__filters">
          {p.search && (
            <div className="adm__search" style={{ flex: '1 1 200px', maxWidth: 320, height: 28 }}>
              <Icon name="search" size={13} style={{ color: 'var(--ink-45)' }} />
              <input value={p.search.value} placeholder={p.search.placeholder} onChange={(e) => p.search!.onChange(e.target.value)} aria-label={p.search.placeholder} />
            </div>
          )}
          {p.chips?.map((c) => <button key={c.label} type="button" className={`chip ${c.on ? 'chip--on' : ''}`} aria-pressed={c.on} onClick={c.onClick}>{c.label}</button>)}
          {p.selects?.map((s, i) => (
            <select key={i} className={`sel ${s.value ? 'sel--on' : ''}`} value={s.value} title={s.title} onChange={(e) => s.onChange(e.target.value)}>
              {s.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ))}
          {p.extra}
        </div>
      )}
      <div className="blk__body">
        {p.loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14 }} aria-busy="true">
            <div className="skel" /><div className="skel" style={{ width: '80%' }} /><div className="skel" style={{ width: '60%' }} />
          </div>
        )}
        {!p.loading && p.error && (
          <div className="blk__err" role="alert">
            <Icon name="warn" size={15} style={{ color: 'var(--conf-low)', flex: 'none', marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--conf-low)' }}>{p.error.title}</div>{p.error.text && <div style={{ fontSize: 11.5, fontWeight: 300, color: 'var(--ink-62)', marginTop: 2 }}>{p.error.text}</div>}</div>
            {p.error.retry && <button type="button" className="btn btn--link" style={{ fontSize: 12 }} onClick={p.error.retry}>Retry</button>}
          </div>
        )}
        {!p.loading && !p.error && p.empty !== undefined && (
          <div className="blk__empty">{p.emptyGood && <Icon name="check" size={15} style={{ color: 'var(--ok)' }} />}<span style={{ textWrap: 'pretty' }}>{p.empty}</span></div>
        )}
        {!p.loading && !p.error && p.empty === undefined && p.children}
      </div>
      {p.foot && <div className="blk__foot">{p.foot}</div>}
    </section>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="adm__grid">{children}</div>;
}
export function Cell({ span = 12, children }: { span?: number; children: ReactNode }) {
  return <div className={`adm__cell ${span <= 4 ? 'adm__cell--narrow' : 'adm__cell--wide'}`} style={{ gridColumn: `span ${span}` }}>{children}</div>;
}

function cellValue(c: Cell | undefined): SortVal {
  if (!c) return null;
  if (c.v !== undefined) return c.v;
  const text = textOf(c.t) || c.pill?.label || textOf(c.node) || c.cls || textOf(c.sub);
  return text ? valueOf(text) : null;
}

export function Table({ cols, rows, minWidth, mobile, sort, onSort }: { cols: Col[]; rows: Row[]; minWidth?: number; mobile?: boolean; sort?: SortState; onSort?: (key: string, firstDir: 'asc' | 'desc') => void }) {
  const [picked, setLocal] = useState<(SortState & { sig: string }) | null>(null);
  const stick = useRef<HTMLDivElement>(null);
  const headGrid = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const bodyGrid = useRef<HTMLDivElement>(null);

  const fit = () => {
    const g = bodyGrid.current, h = headGrid.current;
    if (!g || !h) return;
    const tracks = getComputedStyle(g).gridTemplateColumns;
    if (h.style.gridTemplateColumns !== tracks) h.style.gridTemplateColumns = tracks;
    const w = `${g.getBoundingClientRect().width}px`;
    if (h.style.width !== w) h.style.width = w;
    if (stick.current && body.current) stick.current.scrollLeft = body.current.scrollLeft;
  };
  useLayoutEffect(fit);
  useEffect(() => {
    const g = bodyGrid.current;
    if (!g || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(g);
    return () => ro.disconnect();
  }, [mobile]);

  if (mobile) return <Cards cols={cols} rows={rows} />;
  const grid = cols.map((c) => c.width).join(' ');
  const server = !!onSort;
  const sig = cols.map((c) => c.label).join('|');
  const local = picked?.sig === sig ? picked : null;
  const keyOf = (c: Col, i: number) => (server ? c.sort : c.label && rows.some((r) => cellValue(r.cells[i]) !== null) ? `#${i}` : undefined);
  const active = server ? sort : local ?? undefined;
  const shown = !server && local ? sortRows(rows, (r) => cellValue(r.cells[+local.key.slice(1)]), local.dir) : rows;
  const click = (c: Col, i: number, key: string) => {
    if (server) return onSort!(key, c.sortAsc ? 'asc' : 'desc');
    const first: 'asc' | 'desc' = c.sortAsc ? 'asc' : rows.some((r) => typeof cellValue(r.cells[i]) === 'number') ? 'desc' : 'asc';
    setLocal(local?.key === key ? { key, dir: local.dir === 'asc' ? 'desc' : 'asc', sig } : { key, dir: first, sig });
  };
  const head = (live: boolean) => cols.map((c, i) => {
    const key = keyOf(c, i);
    const on = !!key && active?.key === key;
    return key ? (
      <button key={i} type="button" tabIndex={live ? undefined : -1} className={`tbl__h tbl__h--sort ${on ? 'tbl__h--on' : ''}`} role={live ? 'columnheader' : undefined} aria-sort={live ? (on ? (active?.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined} title="Sort" onClick={live ? () => click(c, i, key) : undefined} style={{ justifyContent: c.align === 'r' ? 'flex-end' : 'flex-start' }}>
        {c.label}<span aria-hidden="true" style={{ opacity: on ? 1 : 0.35 }}>{on ? (active?.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    ) : <span key={i} className="tbl__h" role={live ? 'columnheader' : undefined} style={{ textAlign: c.align === 'r' ? 'right' : 'left' }}>{c.label}</span>;
  });
  return (
    <div className="tbl" role="grid" aria-rowcount={rows.length + 1}>
      <div
        className="tbl__stick"
        ref={stick}
        role="rowgroup"
        onWheel={(e) => { if (body.current && Math.abs(e.deltaX) > Math.abs(e.deltaY)) body.current.scrollLeft += e.deltaX; }}
      >
        <div className="tbl__grid" ref={headGrid} style={{ gridTemplateColumns: grid, minWidth: minWidth ?? 0 }}>
          <div className="tbl__head" role="row">{head(true)}</div>
        </div>
      </div>
      <div className="tbl__scroll" ref={body} onScroll={() => { if (stick.current && body.current) stick.current.scrollLeft = body.current.scrollLeft; }}>
        <div className="tbl__grid" ref={bodyGrid} role="rowgroup" style={{ minWidth: minWidth ?? 0, gridTemplateColumns: grid }}>
          <div className="tbl__head tbl__head--sizer" aria-hidden="true">{head(false)}</div>
          {shown.map((r) => (
            <div key={r.key} className="tbl__item" role="presentation" style={{ background: r.bg }}>
              <div
                className={`tbl__row ${r.onClick ? 'tbl__row--tap' : ''}`}
                role="row"
                tabIndex={r.onClick ? 0 : undefined}
                onClick={r.onClick}
                onKeyDown={(e) => { if (r.onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); r.onClick(); } }}
              >
                {r.cells.map((c, i) => <div key={i} role="gridcell" style={{ minWidth: 0 }}><CellView c={c} right={cols[i]?.align === 'r' && !c.acts} /></div>)}
              </div>
              {r.expanded && r.meta && <pre className="tbl__meta">{r.meta}</pre>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Cards({ cols, rows }: { cols: Col[]; rows: Row[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((r) => {
        const first = r.cells[0];
        const acts = r.cells.find((c) => c.acts)?.acts;
        return (
          <div key={r.key} className="li" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8, padding: '12px 14px', cursor: r.onClick ? 'pointer' : undefined }} onClick={r.onClick} role={r.onClick ? 'button' : undefined} tabIndex={r.onClick ? 0 : undefined}>
            <div style={{ fontSize: 13.5, fontWeight: 600, wordBreak: 'break-all' }} className={first?.mono ? 'mono' : ''}>{first?.t ?? first?.pill?.label}</div>
            {first?.sub && <div className="tbl__s" style={{ marginTop: -6 }}>{first.sub}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '96px minmax(0,1fr)', gap: '6px 10px', alignItems: 'center' }}>
              {r.cells.slice(1).map((c, i) => (cols[i + 1]?.label && !c.acts && (c.t !== undefined || c.pill || c.cls !== undefined || c.dot)) ? (
                <Fragment key={i}><span className="tbl__h" style={{ textTransform: 'uppercase' }}>{cols[i + 1].label}</span><CellView c={c} /></Fragment>
              ) : null)}
            </div>
            {acts && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }} onClick={(e) => e.stopPropagation()}>{acts.map((a) => <button key={a.label} type="button" className={`btn ${a.danger ? 'btn--danger' : ''}`} style={{ minHeight: 40 }} onClick={a.onClick}>{a.label}</button>)}</div>}
          </div>
        );
      })}
    </div>
  );
}

export interface KvRow { k: ReactNode; kSub?: ReactNode; v: Cell; pending?: string; acts?: Act[]; mono?: boolean }
export function Kv({ rows, kw }: { rows: KvRow[]; kw?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((r, i) => (
        <div key={i} className="kv" style={{ ['--kw' as string]: kw }}>
          <div style={{ minWidth: 0 }}><div className={`kv__k ${r.mono ? 'mono' : ''}`}>{r.k}</div>{r.kSub && <div style={{ fontSize: 10.5, fontWeight: 300, color: 'var(--ink-45)', marginTop: 1 }}>{r.kSub}</div>}</div>
          <div className="kv__v"><CellView c={r.v} />{r.pending && <span className="pill pill--warn" style={{ borderRadius: 5 }}>pending {r.pending}</span>}{r.acts && <Acts acts={r.acts} />}</div>
        </div>
      ))}
    </div>
  );
}

export interface ListItem { key: string; t: ReactNode; sub?: ReactNode; right?: ReactNode; pill?: { label: string; tone: Tone }; icon?: string; iconFg?: string; onClick?: () => void; acts?: Act[] }
export function List({ items }: { items: ListItem[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {items.map((it) => {
        const Tag = it.onClick ? 'button' : 'div';
        return (
          <Tag key={it.key} type={it.onClick ? 'button' : undefined} className={`li ${it.onClick ? 'li--tap' : ''}`} onClick={it.onClick}>
            {it.icon && <Icon name={it.icon} size={15} style={{ color: it.iconFg ?? 'var(--ink-55)', flex: 'none' }} />}
            {it.pill && <Pill tone={it.pill.tone}>{it.pill.label}</Pill>}
            <div style={{ flex: 1, minWidth: 0 }}><div className="li__t">{it.t}</div>{it.sub && <div className="li__s">{it.sub}</div>}</div>
            {it.right && <span className="li__r">{it.right}</span>}
            {it.acts && <Acts acts={it.acts} />}
          </Tag>
        );
      })}
    </div>
  );
}

export interface Kpi { label: string; v: ReactNode; sub?: ReactNode; fg?: string; dot?: Tone; onClick?: () => void }
export function Kpis({ items, min }: { items: Kpi[]; min?: number }) {
  return (
    <div className="kpis" style={{ ['--kpi-min' as string]: `${min ?? 140}px` }}>
      {items.map((it) => {
        const Tag = it.onClick ? 'button' : 'div';
        return (
          <Tag key={it.label} type={it.onClick ? 'button' : undefined} className={`kpi ${it.onClick ? 'kpi--tap' : ''}`} onClick={it.onClick}>
            <div className="kpi__label">{it.dot && <Dot tone={it.dot} />}{it.label}</div>
            <div className="kpi__v" style={{ color: it.fg }}>{it.v}</div>
            {it.sub && <div className="kpi__sub">{it.sub}</div>}
          </Tag>
        );
      })}
    </div>
  );
}

export function Strip({ items, acts }: { items: { k: string; v: ReactNode; fg?: string }[]; acts?: Act[] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px 18px', flexWrap: 'wrap', padding: '11px 14px' }}>
      {items.map((it) => <div key={it.k} style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}><span className="tbl__h">{it.k}</span><span style={{ fontSize: 13, fontWeight: 600, color: it.fg ?? 'var(--ink-96)' }}>{it.v}</span></div>)}
      <div style={{ flex: '1 1 0' }} />
      {acts && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{acts.map((a) => <button key={a.label} type="button" className={`btn ${a.primary ? 'btn--primary' : ''} ${a.danger ? 'btn--danger' : ''}`} title={a.title} onClick={a.onClick} disabled={a.disabled}>{a.label}</button>)}</div>}
    </div>
  );
}

export function Banner({ icon = 'info', fg = 'var(--brand)', text, sub, acts }: { icon?: string; fg?: string; text: ReactNode; sub?: ReactNode; acts?: Act[] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 14px' }}>
      <Icon name={icon} size={17} style={{ color: fg, flex: 'none' }} />
      <div style={{ flex: '1 1 240px', minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 600, textWrap: 'pretty' }}>{text}</div>{sub && <div style={{ fontSize: 12, fontWeight: 300, color: 'var(--ink-62)', marginTop: 2, textWrap: 'pretty' }}>{sub}</div>}</div>
      {acts && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{acts.map((a) => <button key={a.label} type="button" className={`btn btn--sm ${a.primary ? 'btn--primary' : ''} ${a.danger ? 'btn--danger' : ''}`} onClick={a.onClick}>{a.label}</button>)}</div>}
    </div>
  );
}

export function Placeholder({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="blk__placeholder">
      <span style={{ font: '600 10px var(--vs-font-mono)', letterSpacing: '.14em', color: 'var(--ink-45)' }}>NOT BUILT YET</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-80)' }}>{text}</span>
      {sub && <span style={{ maxWidth: 420, fontSize: 12, fontWeight: 300, color: 'var(--ink-55)', textWrap: 'pretty' }}>{sub}</span>}
    </div>
  );
}

export interface Series { label: string; vals: number[]; bg: string }
export function Chart({ big, bigSub, series, labels, fmt, height = 84 }: { big?: ReactNode; bigSub?: ReactNode; series: Series[]; labels: string[]; fmt?: (v: number) => string; height?: number }) {
  const n = labels.length;
  const f = fmt ?? ((v: number) => v.toLocaleString());
  let max = 1;
  for (let i = 0; i < n; i++) max = Math.max(max, series.reduce((a, s) => a + (s.vals[i] ?? 0), 0));
  return (
    <div className="chart">
      {(big !== undefined || bigSub) && <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}><span className="chart__big">{big}</span><span style={{ fontSize: 11.5, fontWeight: 300, color: 'var(--ink-55)' }}>{bigSub}</span></div>}
      <div className="chart__bars" style={{ height, gap: n > 40 ? 1 : 2 }} role="img" aria-label={series.map((s) => s.label).join(', ')}>
        {labels.map((l, i) => (
          <div key={i} className="chart__bar" title={`${l} · ${series.map((s) => `${s.label} ${f(s.vals[i] ?? 0)}`).join(', ')}`}>
            {series.map((s, j) => <div key={j} style={{ flex: 'none', height: `${((s.vals[i] ?? 0) / max) * 100}%`, background: s.bg, borderRadius: j === series.length - 1 ? '2px 2px 0 0' : 0 }} />)}
          </div>
        ))}
      </div>
      <div className="chart__x"><span>{labels[0] ?? ''}</span><span>{labels[n - 1] ?? ''}</span></div>
      {series.length > 1 && <div className="chart__legend">{series.map((s) => <span key={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: s.bg }} />{s.label}</span>)}</div>}
    </div>
  );
}

export function HBars({ rows }: { rows: { label: ReactNode; n: number; v: ReactNode; bg?: string; mono?: boolean }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '12px 14px' }}>
      {rows.map((r, i) => (
        <div key={i} className="hbar">
          <span className={r.mono ? 'mono' : ''} style={{ fontSize: 12, color: 'var(--ink-80)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</span>
          <div className="hbar__track"><div style={{ height: '100%', width: `${(r.n / max) * 100}%`, background: r.bg ?? 'var(--brand)', borderRadius: 4 }} /></div>
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-62)', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.v}</span>
        </div>
      ))}
    </div>
  );
}

export interface Span { l: number; w: number; bg: string; title?: string; bd?: string }
export function Spans({ tracks, axis, legend }: { tracks: { label: string; segs: Span[] }[]; axis: string[]; legend?: { label: string; bg: string; bd?: string }[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px' }}>
      {tracks.map((t) => (
        <div key={t.label} style={{ display: 'grid', gridTemplateColumns: '96px minmax(0,1fr)', gap: 10, alignItems: 'center' }}>
          <span className="tbl__h">{t.label}</span>
          <div style={{ position: 'relative', height: 16, borderRadius: 4, background: 'var(--fill-05)' }}>
            {t.segs.map((s, i) => <div key={i} title={s.title} style={{ position: 'absolute', top: 0, bottom: 0, left: `${s.l * 100}%`, width: `${s.w * 100}%`, background: s.bg, borderRadius: 3, border: s.bd ?? 0, boxSizing: 'border-box' }} />)}
          </div>
        </div>
      ))}
      <div style={{ display: 'grid', gridTemplateColumns: '96px minmax(0,1fr)', gap: 10 }}><span /><div className="chart__x">{axis.map((x, i) => <span key={i}>{x}</span>)}</div></div>
      {legend && <div className="chart__legend">{legend.map((l) => <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 8, borderRadius: 2, background: l.bg, border: l.bd ?? 0, boxSizing: 'border-box' }} />{l.label}</span>)}</div>}
    </div>
  );
}

export function Log({ lines }: { lines: { ts?: string; t: string; hl?: boolean; tone?: Tone }[] }) {
  return (
    <div className="log">
      {lines.map((l, i) => (
        <div key={i} className="log__line" style={{ background: l.hl ? 'var(--brand-tint)' : undefined }}>
          {l.ts && <span style={{ flex: 'none', color: 'var(--ink-40)' }}>{l.ts}</span>}
          <span style={{ flex: 1, minWidth: 0, color: l.tone ? TONE_FG[l.tone] : l.hl ? 'var(--ink-96)' : 'var(--ink-70)', fontWeight: l.hl ? 600 : 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{l.t}</span>
        </div>
      ))}
    </div>
  );
}

export function Field({ label, value, onChange, placeholder, type = 'text', mono, autoFocus, onEnter, hint }: { label: ReactNode; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; mono?: boolean; autoFocus?: boolean; onEnter?: () => void; hint?: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input className={`field__input ${mono ? 'mono' : ''}`} type={type} value={value} placeholder={placeholder} autoFocus={autoFocus} autoComplete="off" spellCheck={false} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter(); } }} />
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function Seg<T extends string>({ value, options, onChange, mono }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; mono?: boolean }) {
  return (
    <div className="seg" role="radiogroup">
      {options.map((o) => <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={`seg__opt ${value === o.value ? 'seg__opt--on' : ''} ${mono ? 'mono' : ''}`} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label?: ReactNode }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} style={{ position: 'relative', flex: 'none', width: 36, height: 20, borderRadius: 10, background: on ? 'var(--brand)' : 'var(--fill-14)', transition: 'background .15s' }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.25)', transition: 'left .15s' }} />
      </button>
      {label && <span style={{ fontSize: 11.5, fontWeight: 300, color: 'var(--ink-55)' }}>{label}</span>}
    </label>
  );
}

export function ListMore({ list, noun = 'rows' }: { list: { rows: unknown[]; total: number | null; hasMore: boolean; loadingMore: boolean; loadMore: () => void }; noun?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const { hasMore, loadingMore, loadMore } = list;
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMore || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) loadMore(); }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore, list.rows.length]);
  if (list.total === null || list.total === 0) return null;
  return (
    <div ref={ref} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span>{list.rows.length.toLocaleString()} of {list.total.toLocaleString()} {noun}</span>
      {hasMore && <button type="button" className="btn btn--link" style={{ fontSize: 11.5 }} disabled={loadingMore} onClick={loadMore}>{loadingMore ? 'Loading…' : 'Load more'}</button>}
    </div>
  );
}

const DAY = 86_400_000;
const ymd = (d: Date) => d.toISOString().slice(0, 10);
const PRESETS: { value: string; label: string; days?: number }[] = [
  { value: '', label: 'Any time' }, { value: '0', label: 'Today', days: 0 }, { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 }, { value: '90', label: 'Last 90 days', days: 90 }, { value: 'custom', label: 'Custom…' },
];
function presetOf(from: string, to: string): string {
  if (!from && !to) return '';
  if (to) return 'custom';
  const hit = PRESETS.find((p) => p.days !== undefined && ymd(new Date(Date.now() - p.days * DAY)) === from);
  return hit?.value ?? 'custom';
}

export function DateRange({ from, to, onChange, label = 'Date' }: { from: string; to: string; onChange: (from: string, to: string) => void; label?: string }) {
  const [custom, setCustom] = useState(() => presetOf(from, to) === 'custom');
  const preset = custom ? 'custom' : presetOf(from, to);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <select className={`sel ${from || to ? 'sel--on' : ''}`} title={label} aria-label={label} value={preset} onChange={(e) => {
        const v = e.target.value;
        if (v === 'custom') { setCustom(true); return; }
        setCustom(false);
        const p = PRESETS.find((x) => x.value === v);
        onChange(p?.days !== undefined ? ymd(new Date(Date.now() - p.days * DAY)) : '', '');
      }}>
        {PRESETS.map((p) => <option key={p.value} value={p.value}>{p.value && p.value !== 'custom' ? `${label}: ${p.label.toLowerCase()}` : p.value === 'custom' ? p.label : `${label}: any time`}</option>)}
      </select>
      {custom && (
        <>
          <input type="date" className={`sel ${from ? 'sel--on' : ''}`} aria-label={`${label} from`} value={from} max={to || undefined} onChange={(e) => onChange(e.target.value, to)} />
          <span style={{ fontSize: 11, color: 'var(--ink-45)' }}>to</span>
          <input type="date" className={`sel ${to ? 'sel--on' : ''}`} aria-label={`${label} to`} value={to} min={from || undefined} onChange={(e) => onChange(from, e.target.value)} />
        </>
      )}
    </span>
  );
}
